/**
 * Shade "Secure Now" button: the right half of the split PDP CTA.
 *
 * It is NOT a submit button. On click it drives the SAME product form as the
 * native "Add to Cart" half (form.requestSubmit() -> product-form.js
 * handleSubmit -> /cart/add.js), inheriting all of Horizon's validity,
 * quantity-rule and variant-in-progress queueing for free. When the add
 * succeeds it redirects straight to /checkout (the navigation wins the race
 * against the cart drawer's auto-open, so the shopper goes to checkout, not the
 * drawer).
 *
 * Custom element, self-registering, idempotent. No dependency on shade-cart.js
 * (that pulse is irrelevant here because we navigate away on success).
 */
class SecureNowButton extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {boolean} Guards against double-trigger while an add is in flight. */
  #pending = false;

  connectedCallback() {
    this.#button = this.querySelector('button');
    if (!this.#button) return;
    this.#button.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
  }

  #onClick = () => {
    if (this.#pending) return;
    const form = this.closest('form');
    if (!form) return;

    // Honour native HTML validity (e.g. required recipient fields on gift cards).
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    this.#pending = true;

    // One resolution path for every outcome: the form's own cart:update, a
    // thrown submit, or a timeout if the add never reports back. product-form.js
    // swallows network failures in a bare .catch without dispatching an event,
    // so without the timeout a silent failure would leave the listener attached
    // and #pending stuck true, wedging the button for the rest of the page life.
    let settled = false;
    let timer = 0;
    const cleanup = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      document.removeEventListener('cart:update', onCartUpdate);
      this.#pending = false;
    };

    // Filter to the product-form source so an unrelated cart:update cannot steal
    // the redirect; we resolve on our own add and tear down via cleanup().
    const onCartUpdate = (event) => {
      const data = event.detail && event.detail.data;
      if (!data || data.source !== 'product-form-component') return; // not our add, keep waiting
      cleanup();
      if (data.didError === true) return; // add failed; the form shows the error, stay put
      window.location.href = '/checkout';
    };
    document.addEventListener('cart:update', onCartUpdate);

    // Safety net: if no cart:update arrives (silent network failure or hang),
    // release the listener and the guard so the button stays usable.
    timer = setTimeout(cleanup, 10000);

    // requestSubmit fires the form's submit handler (product-form.js), unlike
    // form.submit() which would bypass it. Fall back to a click on the real
    // submit button if requestSubmit is unavailable; release immediately if the
    // submit throws synchronously rather than wedging the guard.
    try {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        const addBtn = form.querySelector('[ref="addToCartButton"]');
        if (addBtn) addBtn.click();
        else cleanup();
      }
    } catch (error) {
      cleanup();
    }
  };
}

if (!customElements.get('secure-now-button')) {
  customElements.define('secure-now-button', SecureNowButton);
}
